import React, { useState, useEffect } from 'react';
import { adminAPI, invoiceAPI } from '../../services/api';
import { TableSkeleton } from '../../components/skeletons';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

const statusBadge = (status) => {
  const map = {
    paid: { bg: '#dcfce7', color: GREEN, label: 'Paid' },
    pending: { bg: '#fef3c7', color: '#d97706', label: 'Pending' },
    overdue: { bg: '#fee2e2', color: '#dc2626', label: 'Overdue' },
    draft: { bg: '#f3f4f6', color: MUTED, label: 'Draft' },
    generated: { bg: PURPLE + '15', color: PURPLE, label: 'Generated' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: MUTED, label: status || 'Unknown' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
};

export default function InvoiceManagement() {
  useAuth();
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [genForm, setGenForm] = useState({ vendorId: '', month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [generating, setGenerating] = useState(false);

  const loadData = () => {
    Promise.all([adminAPI.allInvoices(), adminAPI.vendors()])
      .then(([iRes, vRes]) => {
        setInvoices(iRes.data.data || iRes.data || []);
        setVendors(vRes.data.data || vRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleGenerate = async () => {
    if (!genForm.vendorId) return;
    setGenerating(true);
    try {
      await invoiceAPI.generate({
        vendorId: genForm.vendorId,
        month: parseInt(genForm.month),
        year: parseInt(genForm.year),
      });
      setShowModal(false);
      setGenForm({ vendorId: '', month: new Date().getMonth() + 1, year: new Date().getFullYear() });
      loadData();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to generate invoice');
    }
    setGenerating(false);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) return <TableSkeleton rows={7} columns={6} style={{ padding: 24 }} />;

  return (
    <div style={{ padding: 24, background: CREAM, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: CHAR, margin: 0 }}>Invoice Management</h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>{invoices.length} invoices total</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          Generate Invoice
        </button>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Invoiced', value: `₹${invoices.reduce((s, i) => s + (i.total || 0), 0).toLocaleString('en-IN')}`, color: GREEN },
          { label: 'GST Collected', value: `₹${invoices.reduce((s, i) => s + (i.gst || i.gstAmount || 0), 0).toLocaleString('en-IN')}`, color: GOLD },
          { label: 'Paid', value: invoices.filter(i => i.status === 'paid').length, color: GREEN },
          { label: 'Pending', value: invoices.filter(i => i.status === 'pending' || i.status === 'generated').length, color: PURPLE },
        ].map((s, i) => (
          <div key={i} className="card" style={{ padding: '14px 18px', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Vendor', 'Period', 'Leads', 'Taxable (\u20B9)', 'GST (\u20B9)', 'Total (\u20B9)', 'Status'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `2px solid ${BORDER}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>No invoices generated yet</td></tr>
              ) : (
                invoices.map(inv => (
                  <tr key={inv._id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: CHAR }}>{inv.vendorName || inv.vendor?.fullName || '-'}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: CHAR }}>
                      {inv.month && inv.year ? `${months[(inv.month - 1)] || inv.month} ${inv.year}` : inv.period || '-'}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: PURPLE }}>{inv.leadCount ?? inv.leads ?? 0}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: CHAR }}>₹{(inv.taxable || inv.subtotal || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: GOLD }}>₹{(inv.gst || inv.gstAmount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 700, color: GREEN }}>₹{(inv.total || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}>{statusBadge(inv.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Generate Invoice Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setShowModal(false)}>
          <div className="card" style={{ width: 420, padding: 28 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: CHAR, marginBottom: 20 }}>
              Generate Invoice
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 4, display: 'block' }}>Vendor *</label>
                <select
                  className="form-input"
                  value={genForm.vendorId}
                  onChange={e => setGenForm({ ...genForm, vendorId: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">Select vendor</option>
                  {vendors.map(v => (
                    <option key={v._id} value={v._id}>{v.fullName || v.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 4, display: 'block' }}>Month</label>
                  <select
                    className="form-input"
                    value={genForm.month}
                    onChange={e => setGenForm({ ...genForm, month: e.target.value })}
                    style={{ width: '100%' }}
                  >
                    {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 4, display: 'block' }}>Year</label>
                  <input
                    className="form-input"
                    type="number"
                    value={genForm.year}
                    onChange={e => setGenForm({ ...genForm, year: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#f3f4f6', color: MUTED, border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating || !genForm.vendorId}
                style={{
                  background: GREEN, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: (generating || !genForm.vendorId) ? 0.6 : 1,
                }}
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
